// components/AddProductToOrderModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Divider,
  Stepper,
  Step,
  StepLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import api from '../services/api';

// Helper function to print added items
const printAddedItems = async (orderId, action, itemsData, language) => {
  try {
    // First, fetch the current order to get printer configurations
    const currentOrder = await api.get(`/orders/${orderId}`);
    const originalPrinters = currentOrder.data.printers || {};
    
    // Create updated printers data
    const updatedPrinters = { ...originalPrinters };
    
    // For adding items, we need to add the new item to the appropriate printer(s)
    itemsData.forEach(newItem => {
      // Determine which printer this item should go to
      // For now, add to all printers or find the right one based on product
      const targetPrinterId = newItem.printer_id || 'default';
      
      if (!updatedPrinters[targetPrinterId]) {
        updatedPrinters[targetPrinterId] = {
          printer_id: targetPrinterId,
          printer_name: targetPrinterId === 'default' ? 'Kitchen' : `Printer ${targetPrinterId}`,
          printer_type: 'kitchen',
          printer_ip: null,
          printer_port: 9100,
          items: []
        };
      }
      
      // Add the new item to the printer's items
      updatedPrinters[targetPrinterId].items.push({
        product_id: newItem.product_id,
        product_name: newItem.product_name,
        product_name_en: newItem.product_name,
        quantity: newItem.new_quantity,
        unit_price: newItem.unit_price,
        total_price: newItem.new_quantity * newItem.unit_price,
        notes: newItem.notes || null,
        selected_size: newItem.selected_size || null,
        selected_extras: newItem.selected_extras || [],
        customization_details: {
          has_customizations: !!(newItem.selected_size || (newItem.selected_extras?.length > 0)),
          size_name: newItem.selected_size?.name_en,
          extras: newItem.selected_extras?.map(e => e.name_en)
        }
      });
    });
    
    // Send the updated printer data to the endpoint
    const response = await api.post(`/admin/orders/${orderId}/print-update`, {
      action: action,
      printers: updatedPrinters,
      items: itemsData
    });
    
    return response.data;
  } catch (error) {
    console.error('Error printing added items:', error);
    return null;
  }
};

const AddProductToOrderModal = ({ open, onClose, orderId, onProductAdded, language, keywordsData, user, isProssessing = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [addingToOrder, setAddingToOrder] = useState(false);
  const [error, setError] = useState('');

  // Parse JSON fields helper
  const parseJsonField = (field) => {
    if (!field) return [];
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch (e) {
        return [];
      }
    }
    return field;
  };

  // Fetch products
  useEffect(() => {
    if (open) {
      fetchProducts();
      fetchCategories();
    }
  }, [open]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', 1);
      params.append('limit', 10000);
      params.append('restaurantId', user.restaurant_id);

      const response = await api.get(`/products?${params}`);
      if (response.data) {
        const parsedProducts = response.data.products.map(product => ({
          ...product,
          sizes: parseJsonField(product.sizes),
          extras: parseJsonField(product.extras)
        }));
        setProducts(parsedProducts);
        setFilteredProducts(parsedProducts);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };


  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Filter products
  useEffect(() => {
    let filtered = [...products];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category_id === parseInt(selectedCategory));
    }
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.name_ar || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProducts(filtered);
  }, [selectedCategory, searchTerm, products]);

  const handleProductClick = (product) => {
    const hasSizes = product.has_sizes && product.sizes && product.sizes.length > 0;
    const hasExtras = product.has_extras && product.extras && product.extras.length > 0;
    
    if (hasSizes || hasExtras) {
      setSelectedProduct(product);
      setSelectedSize(null);
      setSelectedExtras([]);
      setQuantity(1);
      setActiveStep(0);
      setDialogOpen(true);
    } else {
      // Add directly to order
      addProductToOrder(product, null, [], 1);
    }
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    if (selectedProduct?.has_extras) {
      setActiveStep(1);
    } else {
      setActiveStep(2);
    }
  };

  const handleExtraToggle = (extra) => {
    setSelectedExtras(prev => {
      if (prev.find(e => e.id === extra.id)) {
        return prev.filter(e => e.id !== extra.id);
      } else {
        return [...prev, extra];
      }
    });
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const addProductToOrder = async (product, size, extras, qty) => {
    setAddingToOrder(true);
    setError('');
    
    const customizationPrice = (size?.price || 0) + 
      extras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    
    const finalPrice = product.price + customizationPrice;
    const totalPrice = finalPrice * qty;
    
    // Prepare item data for printing
    const newItemData = {
      product_id: product.id,
      product_name: language === 'ar' ? product.name_ar || product.name_en : product.name_en,
      new_quantity: qty,
      unit_price: finalPrice,
      notes: null,
      selected_size: size ? {
        id: size.id,
        name_en: size.name_en,
        name_ar: size.name_ar,
        price: size.price
      } : null,
      selected_extras: extras.map(extra => ({
        id: extra.id,
        name_en: extra.name_en,
        name_ar: extra.name_ar,
        price: extra.price
      })),
      printer_id: product.printer_ids?.[0] || 'default' // Use first printer or default
    };
    
    try {
      // Create order item data
      const orderItemData = {
        product_id: product.id,
        quantity: qty,
        price: finalPrice,
        notes: null,
        selected_size: size ? {
          id: size.id,
          name: size.name_en,
          price: size.price
        } : null,
        selected_extras: extras.map(extra => ({
          id: extra.id,
          name: extra.name_en,
          price: extra.price
        })),
        customization_price: customizationPrice,
        total_price: totalPrice
      };
      
      // API call to add item to order
      await api.post(`/admin/orders/${orderId}/items`, orderItemData);
      
      // Print the added item (only for admin role)
      if ((user?.role === 'admin' || user?.role === 'casher') && isProssessing) {
        await printAddedItems(orderId, 'product_added', [newItemData], language);
      }
      
      // Call the onProductAdded callback to refresh the order and close modal
      if (onProductAdded) {
        await onProductAdded();
      }
      
      // Reset state
      setDialogOpen(false);
      setSelectedProduct(null);
      setSelectedSize(null);
      setSelectedExtras([]);
      setQuantity(1);
      onClose();
      
    } catch (error) {
      console.error('Error adding product to order:', error);
      setError(error.response?.data?.message || 'Failed to add product to order');
    } finally {
      setAddingToOrder(false);
    }
  };

  const handleAddToOrder = () => {
    addProductToOrder(selectedProduct, selectedSize, selectedExtras, quantity);
  };

  const getTotalPrice = () => {
    const basePrice = selectedProduct?.price || 0;
    const sizePrice = selectedSize?.price || 0;
    const extrasPrice = selectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    const unitPrice = basePrice + sizePrice + extrasPrice;
    return unitPrice * quantity;
  };

  const renderCustomizationContent = () => {
    // Size selection step
    if (activeStep === 0 && selectedProduct?.has_sizes && selectedProduct.sizes?.length > 0) {
      return (
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>
            {language === 'ar' ? 'اختر المقاس' : 'Select Size'}
          </Typography>
          <RadioGroup value={selectedSize?.id || ''}>
            {selectedProduct.sizes.map((size) => (
              <FormControlLabel
                key={size.id}
                value={size.id}
                control={<Radio sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }} />}
                onChange={() => setSelectedSize(size)}
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {language === 'ar' ? size.name_ar || size.name_en : size.name_en}
                    </Typography>
                    {size.price > 0 && (
                      <Typography sx={{ color: '#FFD700' }}>+RM {size.price.toFixed(2)}</Typography>
                    )}
                  </Box>
                }
                sx={{ width: '100%', mx: 0, mb: 1 }}
              />
            ))}
          </RadioGroup>
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedSize}
            onClick={() => {
              if (selectedProduct?.has_extras) {
                setActiveStep(1);
              } else {
                setActiveStep(2);
              }
            }}
            sx={{
              mt: 3,
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {language === 'ar' ? 'متابعة' : 'Continue'}
          </Button>
        </Box>
      );
    }
    
    // Extras selection step
    if ((activeStep === 1 || (activeStep === 0 && !selectedProduct?.has_sizes)) && 
        selectedProduct?.has_extras && selectedProduct.extras?.length > 0) {
      return (
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>
            {language === 'ar' ? 'اختر الإضافات' : 'Select Extras'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {selectedProduct.extras.map((extra) => (
              <FormControlLabel
                key={extra.id}
                control={
                  <Checkbox
                    checked={!!selectedExtras.find(e => e.id === extra.id)}
                    onChange={() => handleExtraToggle(extra)}
                    sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {language === 'ar' ? extra.name_ar || extra.name_en : extra.name_en}
                    </Typography>
                    {extra.price > 0 && (
                      <Typography sx={{ color: '#FFD700' }}>+RM {extra.price.toFixed(2)}</Typography>
                    )}
                  </Box>
                }
                sx={{ width: '100%', mx: 0 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {selectedProduct?.has_sizes && (
              <Button
                variant="outlined"
                onClick={() => setActiveStep(0)}
                sx={{ borderColor: '#FFD700', color: '#FFD700' }}
              >
                {language === 'ar' ? 'رجوع' : 'Back'}
              </Button>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={() => setActiveStep(2)}
              sx={{
                flex: 1,
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {language === 'ar' ? 'متابعة' : 'Continue'}
            </Button>
          </Box>
        </Box>
      );
    }
    
    // Quantity selection step
    if (activeStep === 2) {
      return (
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>
            {language === 'ar' ? 'اختر الكمية' : 'Select Quantity'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 3 }}>
            <IconButton
              onClick={() => handleQuantityChange(-1)}
              sx={{ backgroundColor: '#2D2D2D', color: '#FFD700', '&:hover': { backgroundColor: '#3D3D3D' } }}
            >
              <RemoveIcon />
            </IconButton>
            <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 50, textAlign: 'center' }}>
              {quantity}
            </Typography>
            <IconButton
              onClick={() => handleQuantityChange(1)}
              sx={{ backgroundColor: '#2D2D2D', color: '#FFD700', '&:hover': { backgroundColor: '#3D3D3D' } }}
            >
              <AddIcon />
            </IconButton>
          </Box>
          
          <Divider sx={{ my: 2, bgcolor: '#2D2D2D' }} />
          
          {/* Price Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#B3B3B3', mb: 1 }}>
              {language === 'ar' ? 'ملخص السعر' : 'Price Summary'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'السعر الأساسي' : 'Base Price'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>RM {selectedProduct?.price.toFixed(2)}</Typography>
            </Box>
            {selectedSize && selectedSize.price > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'المقاس' : 'Size'}: {selectedSize.name_en}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>+RM {selectedSize.price.toFixed(2)}</Typography>
              </Box>
            )}
            {selectedExtras.map(extra => (
              <Box key={extra.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'إضافة' : 'Extra'}: {extra.name_en}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>+RM {extra.price.toFixed(2)}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1, bgcolor: '#2D2D2D' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600 }}>
                {language === 'ar' ? 'الإجمالي' : 'Total'}
              </Typography>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                RM {getTotalPrice().toFixed(2)}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {(selectedProduct?.has_sizes || selectedProduct?.has_extras) && (
              <Button
                variant="outlined"
                onClick={() => {
                  if (selectedProduct?.has_sizes) setActiveStep(0);
                  else if (selectedProduct?.has_extras) setActiveStep(1);
                }}
                sx={{ borderColor: '#FFD700', color: '#FFD700' }}
              >
                {language === 'ar' ? 'رجوع' : 'Back'}
              </Button>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddToOrder}
              disabled={addingToOrder}
              sx={{
                flex: 1,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 700,
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {addingToOrder ? (
                <CircularProgress size={24} sx={{ color: '#000000' }} />
              ) : (
                `${language === 'ar' ? 'إضافة إلى الطلب' : 'Add to Order'} - RM ${getTotalPrice().toFixed(2)}`
              )}
            </Button>
          </Box>
        </Box>
      );
    }
    
    return null;
  };

  return (
    <>
      {/* Main Product Selection Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: isMobile ? 0 : '20px',
            border: '1px solid #FFD700',
            height: isMobile ? '100%' : '80vh',
            maxHeight: isMobile ? '100%' : '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #2D2D2D',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingBagIcon sx={{ color: '#FFD700' }} />
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'إضافة منتج إلى الطلب' : 'Add Product to Order'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          {/* Search and Filter */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={language === 'ar' ? 'بحث عن منتج...' : 'Search product...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: '#1A1A1A',
                  borderRadius: '25px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
                  '& input': { color: '#FFFFFF' }
                }
              }}
            />
          </Box>
          
          {/* Category Filter */}
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 2 }}>
            <Chip
              label={language === 'ar' ? 'الكل' : 'All'}
              onClick={() => setSelectedCategory('all')}
              color={selectedCategory === 'all' ? 'primary' : 'default'}
              sx={{
                backgroundColor: selectedCategory === 'all' ? '#FFD700' : '#1A1A1A',
                color: selectedCategory === 'all' ? '#000000' : '#FFFFFF',
                '&:hover': { backgroundColor: '#FFD700', color: '#000000' }
              }}
            />
            {categories.map(cat => (
              <Chip
                key={cat.id}
                label={language === 'ar' ? cat.name : cat.name_en}
                onClick={() => setSelectedCategory(cat.id.toString())}
                sx={{
                  backgroundColor: selectedCategory === cat.id.toString() ? '#FFD700' : '#1A1A1A',
                  color: selectedCategory === cat.id.toString() ? '#000000' : '#FFFFFF',
                  '&:hover': { backgroundColor: '#FFD700', color: '#000000' }
                }}
              />
            ))}
          </Box>
          
          {/* Products Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد منتجات' : 'No products found'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: '#1A1A1A',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)',
                        border: '1px solid #FFD700'
                      },
                      position: 'relative'
                    }}
                    onClick={() => handleProductClick(product)}
                  >
                    {(product.has_sizes || product.has_extras) && (
                      <Chip
                        label={language === 'ar' ? 'تخصيص' : 'Customizable'}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          fontWeight: 600,
                          fontSize: '0.65rem'
                        }}
                      />
                    )}
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.name_en}
                          sx={{ width: 70, height: 70, objectFit: 'contain', mb: 1 }}
                        />
                      ) : (
                        <Box sx={{ width: 70, height: 70, bgcolor: '#2D2D2D', borderRadius: 2, mb: 1 }} />
                      )}
                      <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.8rem' }}>
                        {language === 'ar' ? product.name_ar || product.name_en : product.name_en}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        RM {product.price.toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={onClose} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Customization Dialog (Sizes & Extras) */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: '20px',
            border: '1px solid #FFD700'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2D2D2D', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {language === 'ar' ? 'تخصيص المنتج' : 'Customize Product'}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {/* Product Preview */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, pb: 2, borderBottom: '1px solid #2D2D2D' }}>
            {selectedProduct?.imageUrl ? (
              <Box
                component="img"
                src={selectedProduct.imageUrl}
                alt={selectedProduct?.name_en}
                sx={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 2 }}
              />
            ) : (
              <Box sx={{ width: 70, height: 70, bgcolor: '#2D2D2D', borderRadius: 2 }} />
            )}
            <Box>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {language === 'ar' ? selectedProduct?.name_ar || selectedProduct?.name_en : selectedProduct?.name_en}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'السعر الأساسي:' : 'Base price:'} RM {selectedProduct?.price.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          
          {/* Stepper */}
          {(selectedProduct?.has_sizes || selectedProduct?.has_extras) && (
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {selectedProduct?.has_sizes && (
                <Step>
                  <StepLabel StepIconProps={{ sx: { color: '#FFD700' } }}>
                    <Typography sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                      {language === 'ar' ? 'مقاس' : 'Size'}
                    </Typography>
                  </StepLabel>
                </Step>
              )}
              {selectedProduct?.has_extras && (
                <Step>
                  <StepLabel StepIconProps={{ sx: { color: '#FFD700' } }}>
                    <Typography sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                      {language === 'ar' ? 'إضافات' : 'Extras'}
                    </Typography>
                  </StepLabel>
                </Step>
              )}
              <Step>
                <StepLabel StepIconProps={{ sx: { color: '#FFD700' } }}>
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                    {language === 'ar' ? 'الكمية' : 'Quantity'}
                  </Typography>
                </StepLabel>
              </Step>
            </Stepper>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          
          {renderCustomizationContent()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddProductToOrderModal;